import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
    Plus, Search, FileText, ChevronLeft, ChevronRight, 
    ChevronsLeft, ChevronsRight, ArrowUpDown, Filter, RefreshCw, Box, X,
    Globe, Building2
} from 'lucide-react';
import toast from 'react-hot-toast';
import { usePermissions } from './usePermissions';
import { useFontSize } from './FontSizeContext';

const MRList = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    // إعدادات الترجمة والاتجاه
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

    const { canCreate } = usePermissions('mr'); // تأكد إنك ضايف الصلاحية دي
    const fontContext = useFontSize();
    const fontSize = fontContext?.fontSize || 13;

    // --- State الأساسي ---
    const [mrs, setMrs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const session = JSON.parse(localStorage.getItem('user') || '{}');
    const sessionSite = session.site_id || '';
    const sessionOrg = session.org_id || '';
    const isSuperAdmin = session.role?.name === 'SUPER_ADMIN';

    // --- Params State ---
    const searchParams = new URLSearchParams(location.search);
    const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);
    const [limit] = useState(50);
    const [sort, setSort] = useState({ 
        column: searchParams.get('sortBy') || 'mrnum', 
        direction: searchParams.get('sortDir') || 'desc' 
    });

    const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || "");
    const [showAllSites, setShowAllSites] = useState(searchParams.get('all_sites') === 'true');
    const [selectedOrg, setSelectedOrg] = useState(searchParams.get('orgid') || "");
    const [selectedSite, setSelectedSite] = useState(searchParams.get('user_siteid') || "");
    
    const [orgsList, setOrgsList] = useState([]);
    const [sitesList, setSitesList] = useState([]);

    // --- فلاتر الأعمدة ---
    const [filters, setFilters] = useState({
        mrnum: searchParams.get('mrnum') || "",
        description: searchParams.get('description') || "",
        status: searchParams.get('status') || "",
        siteid: searchParams.get('filter_siteid') || ""
    });

    const fetchData = useCallback(async (isAll = showAllSites) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const forceAll = isSuperAdmin && (selectedOrg !== "" || selectedSite !== "");
            const effectiveAll = isAll || forceAll;

            const res = await axios.get('/api/inventory/mr', {
                params: {
                    page,
                    limit,
                    all_sites: Boolean(effectiveAll),
                    user_siteid: effectiveAll ? (selectedSite || filters.siteid) : sessionSite,
                    orgid: effectiveAll ? (selectedOrg || filters.orgid) : sessionOrg,
                    sortBy: sort.column,
                    sortDir: sort.direction,
                    q: searchTerm,
                    mrnum: filters.mrnum,
                    description: filters.description,
                    status: filters.status,
                    filter_siteid: filters.siteid
                },
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setMrs(res.data.data);
            setTotal(res.data.total);
        } catch (err) {
            toast.error(t('mr.messages.loadFailed'));
        } finally {
            setLoading(false);
        }
    }, [page, limit, sort, filters, searchTerm, showAllSites, selectedOrg, selectedSite, sessionSite, sessionOrg, isSuperAdmin, t]);

    // Debounced Effect for Search/Filters
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchData();
        }, 400);
        return () => clearTimeout(timer);
    }, [fetchData]);

    // جلب المنظمات والمواقع للأدمن
    useEffect(() => {
        if (isSuperAdmin) {
            const token = localStorage.getItem('token');
            axios.get('/api/organizations', { headers: { 'Authorization': `Bearer ${token}` } })
            .then(res => setOrgsList(res.data)).catch(err => console.error(err));
        }
    }, [isSuperAdmin]);

    useEffect(() => {
        if (selectedOrg) {
            const org = orgsList.find(o => o.org_id === selectedOrg);
            setSitesList(org ? org.sites : []);
        } else { setSitesList([]); }
    }, [selectedOrg, orgsList]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setPage(1);
    };

    const handleRowClick = (mrnum) => {
        const queryParams = new URLSearchParams({
            page,
            limit,
            q: searchTerm,
            ...filters,
            sortBy: sort.column,
            sortDir: sort.direction,
            all_sites: showAllSites, 
            user_siteid: selectedSite || sessionSite, 
            orgid: selectedOrg || sessionOrg          
        }).toString();
        navigate(`/mr/${mrnum}?${queryParams}`);
    };

    const handleSort = (column) => {
        setSort(prev => ({
            column,
            direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
        setPage(1);
    };

    const clearFilters = () => {
        setFilters({ mrnum: "", description: "", status: "", siteid: "" });
        setSearchTerm("");
        setSelectedOrg("");
        setSelectedSite("");
        setPage(1);
    };

    // Global Events for Multi-Site Toggle
    useEffect(() => {
        const openAllSites = () => { setShowAllSites(true); setPage(1); };
        const viewMySite = () => { setShowAllSites(false); setPage(1); };
        window.addEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
        window.addEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
        return () => {
            window.removeEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
            window.removeEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
        };
    }, []);

    const totalPages = Math.ceil(total / limit) || 1;

    return (
        <div 
            className={`flex flex-col h-full bg-[#f3f4f6] text-[11px] uppercase tracking-tighter overflow-hidden ${isRTL ? 'font-cairo text-right dir-rtl' : 'font-sans text-left'}`} 
            style={{ fontSize: `${fontSize}px` }}
            dir={isRTL ? 'rtl' : 'ltr'}
        >
            
            {/* Toolbar */}
            <div className="bg-white p-3 border-b shadow-sm z-20 space-y-3">
                <div className={`flex justify-between items-center px-4 `}>
                    <div className={`flex items-center gap-6 ${isRTL ? 'mr-4 ' : 'ml-4'}`}>
                        <h2 className={`text-[#1a3353] font-black italic uppercase text-lg flex items-center gap-2 `}>
                            <FileText size={22} className="text-blue-600" /> 
                            {t('mr.headers.mrList')}
                            <RefreshCw 
                                size={18} 
                                className={`cursor-pointer transition-all ${loading ? 'animate-spin text-orange-500' : 'text-blue-400 hover:text-blue-600'} ${isRTL ? 'mr-2' : 'ml-2'}`} 
                                onClick={() => fetchData()} 
                            />
                        </h2>
                        
                        <div className="relative group">
                            <Search className={`absolute top-2.5 text-gray-400 group-focus-within:text-blue-500 ${isRTL ? 'right-3' : 'left-3'}`} size={16} />
                            <input 
                                className={`py-2 border border-gray-300 w-[450px] outline-none focus:border-blue-600 shadow-inner rounded-sm font-black text-blue-900 placeholder:font-normal placeholder:italic uppercase ${isRTL ? 'pr-10 pl-4 text-right' : 'pl-10 pr-4 text-left'}`} 
                                placeholder={t('mr.placeholders.globalSearch')} 
                                value={searchTerm}
                                onChange={(e) => {setSearchTerm(e.target.value); setPage(1);}}
                            />
                        </div>
                    </div>
                    
                    <div className={`flex gap-3 `}>
                        <button onClick={clearFilters} className="flex items-center gap-2 px-4 py-2 text-gray-500 hover:text-red-600 font-bold transition-colors">
                            <X size={16}/> {t('common.cancel')} {/* Reset/Clear */}
                        </button>
                        {canCreate && (
                            <button onClick={() => navigate('/mr/new')} className={`bg-[#1a3353] text-white px-8 py-2.5 rounded-sm font-black flex items-center gap-2 hover:bg-blue-900 shadow-lg active:scale-95 transition-all `}>
                                <Plus size={18} strokeWidth={3} /> {t('mr.actions.newMR')}
                            </button>
                        )}
                    </div>
                </div>

                {/* Advanced Organization/Site Filters (For Admins) */}
                {isSuperAdmin && (
                  <div className={`flex items-center gap-4 bg-gray-50 p-2.5 rounded-sm border border-gray-200 w-fit shadow-sm animate-in slide-in-from-left duration-300 ${isRTL ? 'mr-4 ' : 'ml-4'}`}>
                    <div className={`flex items-center gap-2 border-gray-300 ${isRTL ? 'border-l pl-6 ' : 'border-r pr-6'}`}>
                      <Globe size={16} className="text-blue-600" />
                      <span className="text-gray-400 italic text-[10px]">{t('assets.list.all_orgs')}</span>
                      <select 
                        className={`bg-transparent outline-none font-black text-blue-900 uppercase cursor-pointer ${isRTL ? 'dir-rtl' : ''}`}
                        value={selectedOrg}
                        onChange={(e) => {setSelectedOrg(e.target.value); setPage(1);}}
                      >
                        <option value="">{t('common.all')}</option>
                        {orgsList.map(org => (<option key={org.org_id} value={org.org_id}>{org.org_id}</option>))}
                      </select>
                    </div>

                    <div className={`flex items-center gap-2 border-gray-300 ${isRTL ? 'border-l pl-6 ' : 'border-r pr-6'}`}>
                      <Building2 size={16} className="text-blue-600" />
                      <span className="text-gray-400 italic text-[10px]">{t('assets.list.all_sites')}</span>
                      <select 
                        className={`bg-transparent outline-none font-black text-blue-900 uppercase cursor-pointer disabled:opacity-30 ${isRTL ? 'dir-rtl' : ''}`}
                        value={selectedSite}
                        onChange={(e) => {setSelectedSite(e.target.value); setPage(1);}}
                        disabled={!selectedOrg}
                      >
                        <option value="">{t('common.all')}</option>
                        {sitesList.map(site => (<option key={site.site_id} value={site.site_id}>{site.site_id}</option>))}
                      </select>
                    </div>
                  </div>
                )}
            </div>

            {/* Table Area */}
            <div className="flex-1 overflow-auto p-4 custom-scrollbar">
                <div className="bg-white border rounded-sm shadow-sm overflow-hidden">
                    <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
                        <thead className="bg-[#1a3353] text-white font-bold uppercase sticky top-0 z-10">
                            <tr className={isRTL ? ' text-right' : 'text-left'}>
                                <SortableHeader label={t('mr.tables.mrNum')} sortKey="mrnum" currentSort={sort} onSort={handleSort} isRTL={isRTL} />
                                <SortableHeader label={t('assets.headers.desc')} sortKey="description" currentSort={sort} onSort={handleSort} isRTL={isRTL} />
                                <th className="p-3 border-x border-white/10 text-center">{t('assets.headers.status')}</th>
                                <SortableHeader label={t('mr.tables.enteredDate')} sortKey="entereddate" currentSort={sort} onSort={handleSort} align="center" isRTL={isRTL} />
                                <th className="p-3 text-center border-x border-white/10">{t('assets.headers.site')}</th>
                            </tr>
                            {/* Filter Row */}
                            <tr className={`bg-[#f8fafc] shadow-inner `}>
                                <td className="p-1 border-x border-gray-200">
                                    <input className={`w-full p-1.5 text-blue-900 font-bold outline-none border border-gray-100 focus:border-blue-400 bg-white ${isRTL ? 'text-right' : ''}`} placeholder={t('common.filter')} value={filters.mrnum} onChange={e => handleFilterChange('mrnum', e.target.value)} />
                                </td>
                                <td className="p-1 border-x border-gray-200">
                                    <input className={`w-full p-1.5 text-blue-900 font-bold outline-none border border-gray-100 focus:border-blue-400 bg-white ${isRTL ? 'text-right' : ''}`} placeholder={t('common.filter')} value={filters.description} onChange={e => handleFilterChange('description', e.target.value)} />
                                </td>
                                <td className="p-1 border-x border-gray-200">
                                    <select className={`w-full p-1.5 text-blue-900 font-black outline-none border border-gray-100 bg-white ${isRTL ? 'dir-rtl text-right' : ''}`} value={filters.status} onChange={e => handleFilterChange('status', e.target.value)}>
                                        <option value="">{t('common.all')}</option>
                                        <option value="WAPPR">WAPPR</option>
                                        <option value="APPR">APPR</option>
                                        <option value="CLOSE">CLOSE</option>
                                        <option value="CAN">CANCELLED</option>
                                    </select>
                                </td>
                                <td className="p-1 border-x border-gray-200 bg-gray-100/50"></td>
                                <td className="p-1 border-x border-gray-200">
                                    <input className="w-full p-1.5 text-blue-900 font-bold outline-none border border-gray-100 focus:border-blue-400 bg-white text-center" placeholder={t('common.filter')} value={filters.siteid} onChange={e => handleFilterChange('siteid', e.target.value)} />
                                </td>
                            </tr>
                        </thead>
                        
                        <tbody className="divide-y">
                            {loading ? (
                                <tr><td colSpan="5" className="p-20 text-center font-black text-gray-300 animate-pulse uppercase tracking-[0.2em]">{t('mr.messages.syncing')}</td></tr>
                            ) : mrs.length > 0 ? (
                                mrs.map((mr, i) => (
                                    <tr key={i} className={`hover:bg-blue-50 cursor-pointer transition-colors group ${isRTL ? ' text-right' : 'text-left'}`} onClick={() => handleRowClick(mr.mrnum)}>
                                        <td className="p-3 border-x font-black text-blue-900 group-hover:underline">{mr.mrnum}</td>
                                        <td className="p-3 border-x text-gray-600 font-bold">{mr.description}</td>
                                        <td className="p-3 border-x text-center">
                                            <span className={`px-3 py-1 rounded-full font-black text-[9px] shadow-sm border ${getStatusStyle(mr.status)}`}>
                                                {mr.status}
                                            </span>
                                        </td>
                                        <td className="p-3 border-x text-center text-gray-500 font-bold">{mr.entereddate?.split('T')[0]}</td>
                                        <td className="p-3 text-center border-x text-gray-400 font-black">{mr.siteid}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="5" className="p-20 text-center font-black text-gray-400 italic uppercase">{t('mr.messages.noData')}</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Hybrid Pagination Footer */}
            <div className={`bg-[#1a3353] text-white p-2 px-8 flex justify-between items-center shadow-inner z-30 font-black uppercase tracking-widest text-[10px] `}>
                <div className={`flex items-center gap-6 `}>
                    <div className={`flex items-center gap-2 `}>
                        <Box size={14} className="text-blue-400"/>
                        <span>{t('mr.footer.showing')}: <span className="text-blue-300 mx-1">{total === 0 ? 0 : (page-1)*limit+1}-{Math.min(page*limit, total)}</span></span>
                    </div>
                    <div className={`flex items-center gap-2 border-white/20 px-6 ${isRTL ? 'border-r ' : 'border-l'}`}>
                        <Filter size={14} className="text-orange-400"/>
                        <span>{t('common.totalRecords')}: <span className="text-orange-300 mx-1">{total}</span></span>
                    </div>
                </div>

                <div className={`flex items-center gap-4 `}>
                    <div className={`flex items-center gap-1 bg-black/20 rounded px-2 py-1 border border-white/10 `}>
                        <button disabled={page === 1} onClick={() => setPage(1)} className="p-1.5 hover:text-orange-400 disabled:opacity-20 transition-all"><ChevronsLeft size={18} className={isRTL ? 'rotate-180' : ''}/></button>
                        <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="p-1.5 hover:text-orange-400 disabled:opacity-20 transition-all"><ChevronLeft size={18} className={isRTL ? 'rotate-180' : ''}/></button>
                        <div className={`px-4 text-[11px] font-mono border-x border-white/10 mx-1 flex items-center gap-2 `}>
                            <span className="text-orange-400 font-black text-sm">{page}</span>
                            <span className="text-gray-400 text-[9px]">{t('common.of')} {totalPages}</span>
                        </div>
                        <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="p-1.5 hover:text-orange-400 disabled:opacity-20 transition-all"><ChevronRight size={18} className={isRTL ? 'rotate-180' : ''}/></button>
                        <button disabled={page >= totalPages} onClick={() => setPage(totalPages)} className="p-1.5 hover:text-orange-400 disabled:opacity-20 transition-all"><ChevronsRight size={18} className={isRTL ? 'rotate-180' : ''}/></button>
                    </div>
                    <RefreshCw size={16} className={`cursor-pointer transition-all ${loading ? 'animate-spin text-orange-400' : 'text-blue-300 hover:text-white'}`} onClick={() => fetchData()} />
                </div>
            </div>
        </div>
    );
};

// --- Helpers ---
const SortableHeader = ({ label, sortKey, currentSort, onSort, align = "left", isRTL }) => {
    let justifyClass = 'justify-start';
    if (align === 'center') justifyClass = 'justify-center';
    if (align === 'right') justifyClass = isRTL ? 'justify-start' : 'justify-end';
    if (align === 'left' && isRTL) justifyClass = 'justify-end';

    return (
        <th className={`p-3 border-x border-white/10 cursor-pointer hover:bg-blue-800 transition-colors ${isRTL ? 'text-right' : 'text-left'}`} onClick={() => onSort(sortKey)}>
            <div className={`flex items-center gap-2 ${justifyClass} `}>
                {label}
                <ArrowUpDown size={12} className={currentSort.column === sortKey ? 'text-orange-400' : 'text-white/30'} />
            </div>
        </th>
    );
};

const getStatusStyle = (status) => {
    switch (status) {
        case 'APPR': return 'bg-green-100 text-green-800 border-green-200';
        case 'WAPPR': return 'bg-orange-100 text-orange-800 border-orange-200';
        case 'CAN': return 'bg-red-100 text-red-800 border-red-200';
        case 'CLOSE': return 'bg-gray-100 text-gray-800 border-gray-200';
        default: return 'bg-blue-100 text-blue-800 border-blue-200';
    }
};

export default MRList;